import { NormalizedNumber } from '@sparkdotfi/common-universal'
import { pipe, sumBy } from 'remeda'
import { useChainId } from 'wagmi'

import { ClaimableReward } from '../types'
import { SimplifiedQueryResult, transformSimplifiedQueryResult } from '@/domain/common/query'
import { useOpenDialog } from '@/domain/state/dialogs/hooks'
import { useClaimableRewardsQuery } from '@/features/spark-rewards/data/useClaimableRewardsQuery'
import { claimSparkRewardsDialogConfig } from '@/features/spark-rewards/dialogs/claim-spark-rewards/ClaimSparkRewardsDialog'

export type UseClaimableRewardsSummaryResult = SimplifiedQueryResult<ClaimableRewardsSummary>

export interface ClaimableRewardsSummary {
  usdSum: NormalizedNumber
  isClaimEnabled: boolean
  claimableRewardsWithPrice: ClaimableReward[]
  claimableRewardsWithoutPrice: ClaimableReward[]
  chainId: number
  claimAll: () => void
}

export function useClaimableRewardsSummary(): UseClaimableRewardsSummaryResult {
  const chainId = useChainId()
  const claimableRewardsResult = useClaimableRewardsQuery()
  const openDialog = useOpenDialog()

  return transformSimplifiedQueryResult(claimableRewardsResult, (data) => {
    const claimableRewards = data
      .filter((reward) => reward.chainId === chainId)
      .filter(({ isGeoBlocked }) => !isGeoBlocked)
      .map(({ rewardToken, cumulativeAmount, pendingAmount, preClaimed, chainId }) => {
        const amountToClaim = cumulativeAmount.minus(preClaimed)
        return {
          token: rewardToken,
          amountPending: pendingAmount,
          amountToClaim,
          chainId,
        }
      })
      .filter(({ amountToClaim }) => amountToClaim.gt(0))

    const claimableRewardsWithPrice = claimableRewards.filter(({ token, amountToClaim }) =>
      token.toUSD(amountToClaim).gt(0),
    )
    const usdSum = pipe(
      claimableRewardsWithPrice,
      sumBy(({ token, amountToClaim }) => token.toUSD(amountToClaim).toNumber()),
      NormalizedNumber,
    )

    const isClaimEnabled = pipe(
      claimableRewards,
      sumBy(({ amountToClaim }) => amountToClaim.toNumber()),
      Boolean,
    )

    const claimableRewardsWithoutPrice = claimableRewards.filter(
      ({ token, amountToClaim }) => amountToClaim.gt(0) && token.toUSD(amountToClaim).eq(0),
    )

    function claimAll(): void {
      openDialog(claimSparkRewardsDialogConfig, {
        tokensToClaim: claimableRewards.map(({ token }) => token),
      })
    }

    return {
      usdSum,
      isClaimEnabled,
      claimableRewardsWithPrice,
      claimableRewardsWithoutPrice,
      chainId,
      claimAll,
    }
  })
}
